import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { differenceInSeconds } from 'date-fns';
import { countdownParts } from '../../../../../utils/countdownParts';
import { useNetwork } from '../../../../../utils/network';
import { GiveawayCountdownTimer } from '../NewLayout/RafflePage/RaffleHero/GiveawayCountdownTimer';

// Three sizes so a block can rank its own timers: the jackpot reads as the
// headline draw, the subscriber draws below it as the supporting ones. `md` is
// the original size and stays the default for every existing caller.
const TIMER_SIZES = {
  md: { num: { xs: 20, sm: 26 }, lab: { xs: 9, sm: 10 }, py: { xs: 0.85, sm: 1.05 } },
  lg: { num: { xs: 26, sm: 34 }, lab: { xs: 10, sm: 11 }, py: { xs: 1.1, sm: 1.35 } }
} as const;

// Arena-tone draw timer — mirrors the member dashboard's countdown (translucent
// boxes, white numerals, no separators) and lays the four fields out as a fixed
// 4-column grid so they never wrap onto a second line inside the narrow cards.
const ArenaDrawTimer = ({
  nextGiveaway,
  size = 'md'
}: {
  nextGiveaway: Date;
  size?: keyof typeof TIMER_SIZES;
}) => {
  const [secs, setSecs] = useState(() =>
    Math.max(0, differenceInSeconds(nextGiveaway, new Date()))
  );
  useEffect(() => {
    setSecs(Math.max(0, differenceInSeconds(nextGiveaway, new Date())));
  }, [nextGiveaway]);
  useEffect(() => {
    if (secs <= 0) return;
    const t = setTimeout(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [secs]);

  const d = countdownParts(secs);
  const pad = (n?: number) => String(n ?? 0).padStart(2, '0');
  const fields = [
    { label: 'Days', value: d.days },
    { label: 'Hours', value: d.hours },
    { label: 'Mins', value: d.minutes },
    { label: 'Secs', value: d.seconds }
  ];

  const s = TIMER_SIZES[size];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: { xs: 0.75, sm: 1 }
      }}
    >
      {fields.map((f) => (
        <Box
          key={f.label}
          sx={{
            py: s.py,
            px: 0.5,
            minWidth: 0,
            borderRadius: '10px',
            textAlign: 'center',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)'
          }}
        >
          <Typography
            sx={{
              fontSize: s.num,
              lineHeight: 1,
              fontWeight: 900,
              color: '#ffffff',
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {pad(f.value)}
          </Typography>
          <Typography
            sx={{
              mt: 0.6,
              fontSize: s.lab,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              fontWeight: 900,
              color: 'rgba(255,255,255,0.7)'
            }}
          >
            {f.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

type ActiveGiveaway = {
  id: number;
  giveawayName: string;
  endDate: string;
  prizeAmount: number | string;
};

type GiveawayPrizeTimersProps = {
  prizeAmount?: number;
  limit?: number;
  title?: string;
  subtitle?: string;
  emptyText?: string;
  compact?: boolean;
  tone?: 'light' | 'dark';
  /** Lead with the jackpot draw above the subscriber draws. Opt-in: the other
   *  three callers of this component only ever want the $1,000 timers. */
  jackpot?: boolean;
  /** Headline above the jackpot timer, shown only when `jackpot` is set. */
  jackpotHeading?: string;
  /** One draw per row instead of a side-by-side grid. For narrow containers —
   *  three of these cards across a half-width column squeezes the numerals into
   *  their boxes. The card itself is unchanged, just full width. Opt-in so the
   *  full-width callers keep their row. */
  stackDraws?: boolean;
};

/** The jackpot draw, from the same endpoint the Giveaway page's hero uses so the
 *  two countdowns can never disagree. `prizeAmount` comes back from the server —
 *  the amount is not hardcoded here. */
type JackpotDraw = { endDate: string; prizeAmount: number | string };

const formatPrize = (value: number | string) =>
  `A$${Number(value || 0).toLocaleString('en-AU', {
    maximumFractionDigits: 0
  })}`;

const GiveawayPrizeTimers = ({
  prizeAmount = 1000,
  limit = 3,
  title = 'Subscribers get exclusive access to more giveaways',
  subtitle = 'Subscription members get exclusive access into extra $1,000 draws.',
  emptyText = '',
  compact = false,
  tone = 'light',
  jackpot = false,
  jackpotHeading = '',
  stackDraws = false
}: GiveawayPrizeTimersProps) => {
  const { get } = useNetwork();
  const [giveaways, setGiveaways] = useState<ActiveGiveaway[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [jackpotDraw, setJackpotDraw] = useState<JackpotDraw | null>(null);
  const [jackpotLoaded, setJackpotLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadGiveaways = async () => {
      try {
        const data = await get(
          `/giveaway/active?prizeAmount=${prizeAmount}&limit=${limit}`,
          true
        );

        if (!cancelled) {
          setGiveaways(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Unable to load prize giveaway timers:', error);
        if (!cancelled) setGiveaways([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    loadGiveaways();
    return () => {
      cancelled = true;
    };
  }, [limit, prizeAmount]);

  useEffect(() => {
    if (!jackpot) return;
    let cancelled = false;

    const loadJackpot = async () => {
      try {
        const data = await get('/giveaway/countdown', true);
        // 404s when no jackpot is running — leave the block out rather than
        // render a countdown to the epoch
        if (!cancelled) setJackpotDraw(data?.endDate ? data : null);
      } catch (error) {
        console.error('Unable to load the jackpot giveaway timer:', error);
        if (!cancelled) setJackpotDraw(null);
      } finally {
        if (!cancelled) setJackpotLoaded(true);
      }
    };

    loadJackpot();
    return () => {
      cancelled = true;
    };
  }, [jackpot]);

  const jackpotDate = useMemo(() => {
    if (!jackpotDraw) return null;
    const d = new Date(jackpotDraw.endDate);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [jackpotDraw]);

  const validGiveaways = useMemo(
    () =>
      giveaways
        .map((giveaway) => ({
          ...giveaway,
          drawDate: new Date(giveaway.endDate)
        }))
        .filter((giveaway) => !Number.isNaN(giveaway.drawDate.getTime())),
    [giveaways]
  );

  const isDark = tone === 'dark';

  // Both fetches have to settle before we can conclude there is nothing to show —
  // otherwise the jackpot block would be dropped whenever the $1,000 draws resolve first.
  const settled = loaded && (!jackpot || jackpotLoaded);
  if (settled && validGiveaways.length === 0 && !emptyText && !jackpotDate) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        py: compact ? { xs: 2, md: 2.4 } : { xs: 4, md: 5 },
        px: compact ? { xs: 1.5, md: 2 } : { xs: 2, md: 3 },
        borderRadius: compact ? 3 : 4,
        background: isDark
          ? 'linear-gradient(180deg, rgba(11,17,25,0.74), rgba(7,11,17,0.78))'
          : 'linear-gradient(180deg, #ffffff 0%, #f3f8fb 100%)',
        border: isDark
          ? '1px solid rgba(56,232,255,0.22)'
          : '1px solid rgba(15,23,42,0.08)',
        boxShadow: compact
          ? 'none'
          : isDark
            ? '0 18px 40px -26px rgba(0,0,0,0.9)'
            : '0 18px 44px rgba(15,23,42,0.08)',
        color: isDark ? '#e8f4fb' : '#0f172a'
      }}
    >
      <Box sx={{ maxWidth: compact ? 1440 : 1180, mx: 'auto' }}>
        {title && (
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: compact ? { xs: 24, md: 34 } : { xs: 28, md: 44 },
              lineHeight: 1.05,
              fontWeight: 1000,
              color: isDark ? '#ffffff' : '#0f172a'
            }}
          >
            {title}
          </Typography>
        )}
        {/* ---- the jackpot draw, leading the block ---- */}
        {jackpotDate && (
          <Box
            sx={{
              mt: title ? 2 : 0,
              p: compact ? { xs: 1.75, md: 2.25 } : { xs: 2.25, md: 3 },
              borderRadius: 3,
              // gold rather than the cyan the $1,000 cards use, so the jackpot
              // reads as the bigger prize at a glance instead of one more timer
              background: isDark
                ? 'linear-gradient(160deg, rgba(255,207,64,0.12), rgba(9,14,20,0.94))'
                : 'linear-gradient(160deg, rgba(255,207,64,0.16), rgba(255,255,255,0.96))',
              border: isDark
                ? '1px solid rgba(255,207,64,0.38)'
                : '1px solid rgba(202,138,4,0.28)',
              boxShadow: isDark
                ? '0 20px 44px -26px rgba(255,207,64,0.45)'
                : '0 14px 32px rgba(15,23,42,0.09)'
            }}
          >
            {jackpotHeading && (
              <Typography
                sx={{
                  textAlign: 'center',
                  mx: 'auto',
                  maxWidth: 720,
                  fontSize: compact ? { xs: 17, md: 21 } : { xs: 18, md: 24 },
                  lineHeight: 1.3,
                  fontWeight: 900,
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              >
                {jackpotHeading}
              </Typography>
            )}
            <Typography
              sx={{
                mt: jackpotHeading ? 0.75 : 0,
                mb: 1.5,
                textAlign: 'center',
                fontSize: compact ? { xs: 32, md: 40 } : { xs: 34, md: 46 },
                lineHeight: 1,
                fontWeight: 1000,
                color: isDark ? '#ffcf40' : '#0f172a'
              }}
            >
              {formatPrize(jackpotDraw?.prizeAmount ?? 0)}
            </Typography>
            {isDark ? (
              <ArenaDrawTimer nextGiveaway={jackpotDate} size="lg" />
            ) : (
              <GiveawayCountdownTimer nextGiveaway={jackpotDate} isDense={compact} />
            )}
          </Box>
        )}

        <Typography
          sx={{
            // the jackpot block is a boxed panel, so the copy under it needs more
            // room than it does under a bare title or it reads as part of the box
            mt: jackpotDate
              ? { xs: 4, md: 5 }
              : title
                ? { xs: 2, md: 2.4 }
                : 0,
            mx: 'auto',
            maxWidth: compact ? 1120 : 860,
            textAlign: 'center',
            fontSize: compact ? { xs: 17, md: 24 } : { xs: 16, md: 21 },
            lineHeight: 1.45,
            fontWeight: 800,
            color: isDark ? 'rgba(255,255,255,0.78)' : '#475569'
          }}
        >
          {subtitle}
        </Typography>

        {validGiveaways.length > 0 ? (
          <Box
            sx={{
              mt: compact ? 2.5 : 3.5,
              display: 'grid',
              gridTemplateColumns: stackDraws
                ? '1fr'
                : {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    xl: `repeat(${Math.min(validGiveaways.length, 3)}, minmax(0, 1fr))`
                  },
              gap: { xs: 1.5, md: 2 }
            }}
          >
            {validGiveaways.map((giveaway, index) => (
              <Box
                key={giveaway.id}
                sx={{
                  p: compact ? { xs: 1.35, md: 1.6 } : { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  background: isDark
                    ? 'linear-gradient(180deg, rgba(17,25,37,0.92), rgba(9,14,20,0.94))'
                    : 'rgba(255,255,255,0.92)',
                  border: isDark
                    ? '1px solid rgba(56,232,255,0.2)'
                    : '1px solid rgba(8,145,178,0.16)',
                  boxShadow: isDark
                    ? '0 16px 34px -22px rgba(0,0,0,0.9)'
                    : '0 14px 30px rgba(15,23,42,0.07)'
                }}
              >
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontSize: compact ? { xs: 12, md: 13 } : { xs: 13, md: 14 },
                    letterSpacing: isDark ? '0.18em' : '0.08em',
                    textTransform: 'uppercase',
                    fontWeight: 1000,
                    fontFamily: isDark
                      ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
                      : undefined,
                    color: isDark ? '#38e8ff' : '#0891b2'
                  }}
                >
                  Subscriber draw {index + 1}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.4,
                    mb: compact ? 1.2 : 1.6,
                    textAlign: 'center',
                    fontSize: compact ? { xs: 22, md: 26 } : { xs: 26, md: 32 },
                    lineHeight: 1,
                    fontWeight: 1000,
                    color: isDark ? '#ffcf40' : '#0f172a'
                  }}
                >
                  {formatPrize(giveaway.prizeAmount)}
                </Typography>
                {isDark ? (
                  /* one step down from the jackpot's `lg` — same shape as the
                     $10,000 card, visibly the smaller prize */
                  <ArenaDrawTimer nextGiveaway={giveaway.drawDate} size="md" />
                ) : (
                  <GiveawayCountdownTimer
                    nextGiveaway={giveaway.drawDate}
                    isTiny
                    isDense={compact}
                  />
                )}
              </Box>
            ))}
          </Box>
        ) : (
          loaded && (
            <Typography
              sx={{
                mt: 3,
                textAlign: 'center',
                fontSize: 16,
                fontWeight: 800,
                color: isDark ? 'rgba(255,255,255,0.72)' : '#64748b'
              }}
            >
              {emptyText}
            </Typography>
          )
        )}
      </Box>
    </Box>
  );
};

export default GiveawayPrizeTimers;
